import { DOMSerializer } from "@tiptap/pm/model";
import { absolutePositionToRelativePosition, ySyncPluginKey } from "@tiptap/y-tiptap";
import * as Y from "yjs";

const LOCKED_INLINE_NODE_TYPES = new Set(["referenceCitation", "mention", "emoji"]);
const LOCKED_BLOCK_NODE_TYPES = new Set(["noteImage", "noteVideo", "table"]);

function uint8ArrayToBase64(bytes) {
    let binary = "";
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
        const chunk = bytes.subarray(index, index + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
    }
    return window.btoa(binary);
}

function attrsWithoutEmptyValues(attrs = {}) {
    return Object.fromEntries(
        Object.entries(attrs || {}).filter(([, value]) => value !== null && value !== undefined && value !== ""),
    );
}

function getNodeText(node) {
    if (!node) return "";
    if (node.type === "text") return node.text || "";
    return (node.content || []).map(getNodeText).join("");
}

function getLockedTokenLabel(node, getCitationLabel) {
    const attrs = node.attrs || {};
    if (node.type === "referenceCitation") {
        const occurrenceId = attrs.occurrenceId ? String(attrs.occurrenceId) : "";
        return getCitationLabel?.(occurrenceId) || (attrs.referenceNo ? `[${attrs.referenceNo}]` : "[ref]");
    }
    if (node.type === "noteImage") return attrs.alt || "이미지";
    if (node.type === "noteVideo") return "비디오";
    if (node.type === "table") return "표";
    return getNodeText(node) || node.type;
}

function createLockedToken(node, context) {
    const id = `locked-${context.lockedTokenIndex}`;
    context.lockedTokenIndex += 1;
    return {
        id,
        type: node.type,
        locked: true,
        label: getLockedTokenLabel(node, context.getCitationLabel),
        attrs: attrsWithoutEmptyValues(node.attrs),
        json: node,
    };
}

function collectTokensFromNode(node, tokens, context) {
    if (!node) return;
    if (node.type === "text") {
        tokens.push({
            type: "text",
            text: node.text || "",
            marks: node.marks || [],
        });
        return;
    }

    if (LOCKED_INLINE_NODE_TYPES.has(node.type) || LOCKED_BLOCK_NODE_TYPES.has(node.type)) {
        tokens.push(createLockedToken(node, context));
        return;
    }

    if (node.content?.length) {
        const group = {
            type: node.type,
            attrs: attrsWithoutEmptyValues(node.attrs),
            children: [],
        };
        node.content.forEach((child) => collectTokensFromNode(child, group.children, context));
        tokens.push(group);
        return;
    }

    tokens.push({
        type: node.type,
        attrs: attrsWithoutEmptyValues(node.attrs),
        text: getNodeText(node),
    });
}

function escapeAttribute(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function tokenToProtectedMarkup(token) {
    if (token.locked) {
        return `<locked-node id="${escapeAttribute(token.id)}" type="${escapeAttribute(token.type)}">${escapeAttribute(
            token.label,
        )}</locked-node>`;
    }
    if (token.type === "text") {
        return token.text || "";
    }
    if (Array.isArray(token.children)) {
        return tokensToProtectedMarkup(token.children);
    }
    return token.text || "";
}

function tokensToProtectedMarkup(tokens = [], { preserveBlockBoundary = false } = {}) {
    return tokens.map(tokenToProtectedMarkup).join(preserveBlockBoundary ? "\n" : "");
}

export function createSelectedHtml(editor, slice) {
    if (!editor || !slice) return "";
    try {
        const serializer = DOMSerializer.fromSchema(editor.schema);
        const fragment = serializer.serializeFragment(slice.content);
        const container = document.createElement("div");
        container.appendChild(fragment);
        return container.innerHTML;
    } catch (_error) {
        return "";
    }
}

export function createNoteSelectionPayload(editor, { getCitationLabel } = {}) {
    const selection = editor?.state?.selection;
    if (!editor || !selection || selection.empty) return null;

    const from = Math.min(selection.from, selection.to);
    const to = Math.max(selection.from, selection.to);
    const slice = selection.content();
    const selectedJson = {
        type: "fragment",
        content: slice.content.toJSON() || [],
    };
    const selectedTokens = [];
    const tokenContext = { getCitationLabel, lockedTokenIndex: 0 };
    selectedJson.content.forEach((node) => collectTokensFromNode(node, selectedTokens, tokenContext));

    return {
        from,
        to,
        hasSelection: true,
        selectedText: editor.state.doc.textBetween(from, to, "\n"),
        selectedJson,
        selectedHtml: createSelectedHtml(editor, slice),
        selectedTokens,
        selectedMarkup: tokensToProtectedMarkup(selectedTokens, { preserveBlockBoundary: true }),
    };
}

function createTextQuote(doc, from, to) {
    const prefixFrom = Math.max(0, from - 48);
    const suffixTo = Math.min(doc.content.size, to + 48);
    return {
        prefix: doc.textBetween(prefixFrom, from, "\n"),
        exact: doc.textBetween(from, to, "\n"),
        suffix: doc.textBetween(to, suffixTo, "\n"),
    };
}

function encodeRelativePosition(relativePosition) {
    if (!relativePosition) return "";
    return uint8ArrayToBase64(Y.encodeRelativePosition(relativePosition));
}

export function createNoteReviewAnchor(editor, selectionPayload = null) {
    const selection = editor?.state?.selection;
    const ystate = editor?.state ? ySyncPluginKey.getState(editor.state) : null;
    const yXmlFragment = ystate?.type;
    const mapping = ystate?.binding?.mapping;
    if (!editor || !selection || selection.empty || !yXmlFragment || !mapping) return null;

    const from = Math.min(selection.from, selection.to);
    const to = Math.max(selection.from, selection.to);
    const relativeFrom = absolutePositionToRelativePosition(from, yXmlFragment, mapping);
    const relativeTo = absolutePositionToRelativePosition(to, yXmlFragment, mapping);
    const selectedText = selectionPayload?.selectedText || editor.state.doc.textBetween(from, to, "\n");
    return {
        from,
        to,
        relative_from: encodeRelativePosition(relativeFrom),
        relative_to: encodeRelativePosition(relativeTo),
        selected_text: selectedText,
        selected_json: selectionPayload?.selectedJson || null,
        selected_markup: selectionPayload?.selectedMarkup || "",
        text_quote: createTextQuote(editor.state.doc, from, to),
    };
}

export function selectionHasLockedTokens(selectionPayload) {
    const visit = (tokens = []) =>
        tokens.some((token) => token.locked || (Array.isArray(token.children) && visit(token.children)));
    return visit(selectionPayload?.selectedTokens || []);
}

export function getLockedTokenLabels(selectionPayload) {
    const labels = [];
    const visit = (tokens = []) => {
        tokens.forEach((token) => {
            if (token.locked) {
                labels.push(token.label || token.type || "");
            }
            if (Array.isArray(token.children)) {
                visit(token.children);
            }
        });
    };
    visit(selectionPayload?.selectedTokens || []);
    return labels.filter(Boolean);
}

export function getLockedTokens(selectionPayload) {
    const lockedTokens = [];
    const visit = (tokens = []) => {
        tokens.forEach((token) => {
            if (token.locked) {
                lockedTokens.push(token);
            }
            if (Array.isArray(token.children)) {
                visit(token.children);
            }
        });
    };
    visit(selectionPayload?.selectedTokens || []);
    return lockedTokens;
}

function parseLockedNodeAttributes(rawAttrs) {
    const attrs = {};
    String(rawAttrs || "").replace(/([A-Za-z0-9_-]+)=["']([^"']*)["']/g, (_match, key, value) => {
        attrs[key] = value;
        return "";
    });
    return attrs;
}

export function parseAIEditReplacementSegments(replacementText) {
    const source = String(replacementText || "");
    const segments = [];
    const pattern = /<locked-node\b([^>]*)>([\s\S]*?)<\/locked-node>/gi;
    let lastIndex = 0;
    let match = pattern.exec(source);

    while (match) {
        if (match.index > lastIndex) {
            segments.push({
                type: "text",
                text: source.slice(lastIndex, match.index),
            });
        }
        const attrs = parseLockedNodeAttributes(match[1]);
        segments.push({
            type: "locked",
            id: attrs.id || "",
            nodeType: attrs.type || "",
            label: match[2] || "",
        });
        lastIndex = pattern.lastIndex;
        match = pattern.exec(source);
    }

    if (lastIndex < source.length) {
        segments.push({
            type: "text",
            text: source.slice(lastIndex),
        });
    }

    return segments;
}

function getPlainTextFromReplacementSegments(replacementText) {
    return parseAIEditReplacementSegments(replacementText)
        .map((segment) => (segment.type === "locked" ? segment.label : segment.text || ""))
        .join("");
}

export function validateAIEditTextReplacement(selectionPayload, replacementText) {
    const lockedTokens = getLockedTokens(selectionPayload);
    if (!lockedTokens.length) return "";

    const replacementLockedSegments = parseAIEditReplacementSegments(replacementText).filter(
        (segment) => segment.type === "locked",
    );

    if (lockedTokens.length !== replacementLockedSegments.length) {
        return "AI 편집 결과의 인용, 이미지, 표 위치가 원본과 달라 반영할 수 없습니다.";
    }

    const mismatchedToken = lockedTokens.find((token, index) => {
        const segment = replacementLockedSegments[index];
        return !segment || segment.id !== token.id || (segment.nodeType && segment.nodeType !== token.type);
    });

    if (mismatchedToken) {
        return "AI 편집 결과의 인용, 이미지, 표 위치가 원본과 달라 반영할 수 없습니다.";
    }
    return "";
}

export function buildAppendContent(replacementText) {
    const plainText = getPlainTextFromReplacementSegments(replacementText);
    const lines = plainText.split(/\n+/).filter(Boolean);
    if (!lines.length) return "";
    return lines.map((line) => ({
        type: "paragraph",
        content: [{ type: "text", text: line }],
    }));
}

function isLockedNodeJson(node) {
    return LOCKED_INLINE_NODE_TYPES.has(node?.type) || LOCKED_BLOCK_NODE_TYPES.has(node?.type);
}

function getTextSegments(replacementText) {
    return String(replacementText || "")
        .split(/\n+/)
        .filter((line, index, lines) => line || index < lines.length - 1);
}

function cloneNodeWithReplacementText(node, textQueue) {
    if (!node) return node;
    if (node.type === "text") {
        const nextText = textQueue.shift();
        if (!nextText) return null;
        return {
            ...node,
            text: nextText,
        };
    }
    if (isLockedNodeJson(node)) return node;
    if (!Array.isArray(node.content)) {
        return node;
    }
    return {
        ...node,
        content: node.content.map((child) => cloneNodeWithReplacementText(child, textQueue)).filter(Boolean),
    };
}

function consumeReplacementTextUntilLocked(segmentQueue) {
    const textParts = [];
    while (segmentQueue.length && segmentQueue[0].type === "text") {
        textParts.push(segmentQueue.shift().text);
    }
    return textParts.join("");
}

function splitReplacementSegmentsIntoBlocks(segments) {
    const blocks = [[]];
    segments.forEach((segment) => {
        if (segment.type !== "text") {
            blocks[blocks.length - 1].push(segment);
            return;
        }
        const parts = String(segment.text || "").split(/\n+/);
        parts.forEach((part, index) => {
            if (index > 0) {
                blocks.push([]);
            }
            if (part) {
                blocks[blocks.length - 1].push({
                    type: "text",
                    text: part,
                });
            }
        });
    });
    return blocks.filter((block, index) => block.length || index === 0);
}

function cloneNodeWithReplacementSegments(node, segmentQueue) {
    if (!node) return node;
    if (node.type === "text") {
        const nextText = consumeReplacementTextUntilLocked(segmentQueue);
        if (!nextText) return null;
        return {
            ...node,
            text: nextText,
        };
    }
    if (isLockedNodeJson(node)) {
        if (segmentQueue[0]?.type === "locked") {
            segmentQueue.shift();
        }
        return node;
    }
    if (!Array.isArray(node.content)) {
        return node;
    }
    return {
        ...node,
        content: node.content.map((child) => cloneNodeWithReplacementSegments(child, segmentQueue)).filter(Boolean),
    };
}

function getSegmentsText(segments = []) {
    return segments.map((segment) => (segment.type === "locked" ? segment.label : segment.text || "")).join("");
}

function appendTextToLastTextNode(nodes, text) {
    if (!text) return false;
    for (let index = nodes.length - 1; index >= 0; index -= 1) {
        const node = nodes[index];
        if (node?.type === "text") {
            node.text = [node.text, text].filter(Boolean).join("\n");
            return true;
        }
        if (Array.isArray(node?.content) && appendTextToLastTextNode(node.content, text)) {
            return true;
        }
    }
    return false;
}

function appendRemainingText(content, segmentQueue) {
    const remainingText = getSegmentsText(segmentQueue);
    if (!remainingText) return content;
    if (appendTextToLastTextNode(content, remainingText)) return content;
    return [
        ...content,
        {
            type: "paragraph",
            content: [{ type: "text", text: remainingText }],
        },
    ];
}

export function buildStructuredReplacementContent(selectionPayload, replacementText) {
    const sourceContent = selectionPayload?.selectedJson?.content;
    if (!Array.isArray(sourceContent) || !sourceContent.length) {
        return String(replacementText || "");
    }
    const lockedTokens = getLockedTokens(selectionPayload);
    if (lockedTokens.length) {
        const blockQueues = splitReplacementSegmentsIntoBlocks(parseAIEditReplacementSegments(replacementText));
        const content = sourceContent
            .map((node, index) => cloneNodeWithReplacementSegments(node, [...(blockQueues[index] || [])]))
            .filter(Boolean);
        const extraSegments = blockQueues.slice(sourceContent.length).flat();
        return appendRemainingText(content, extraSegments);
    }

    const textQueue = getTextSegments(replacementText);
    const content = sourceContent.map((node) => cloneNodeWithReplacementText(node, textQueue)).filter(Boolean);
    if (textQueue.length) {
        const lastNode = content[content.length - 1];
        if (lastNode?.content?.length) {
            const lastTextNode = [...lastNode.content].reverse().find((child) => child.type === "text");
            if (lastTextNode) {
                lastTextNode.text = [lastTextNode.text, ...textQueue].filter(Boolean).join("\n");
            }
        }
    }
    return content;
}
